/**
 * 统一日志工具
 *
 * 功能：
 * 1. 统一的日志前缀
 * 2. 可配置的日志开关
 * 3. 支持多个日志级别
 * 4. 环境自动识别（开发/生产）
 * 5. 支持命名空间（便于分模块控制）
 *
 * 使用示例：
 * ```js
 * import { createLogger } from '@/utils/logger'
 *
 * const logger = createLogger('AgoraRTC')
 * logger.log('Initialized')
 * logger.warn('Low network quality')
 * logger.error('Connection failed', error)
 * ```
 */

// ========== 全局配置 ==========

/**
 * 日志级别
 */
export const LogLevel = {
  NONE: 0,    // 关闭所有日志
  ERROR: 1,   // 只显示错误
  WARN: 2,    // 显示警告和错误
  INFO: 3,    // 显示信息、警告和错误
  DEBUG: 4,   // 显示所有日志
}

/**
 * 全局日志配置
 */
const globalConfig = {
  // 默认日志级别（开发环境显示所有，生产环境只显示错误）
  level: import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.ERROR,

  // 是否启用日志（可通过 URL 参数或 localStorage 覆盖）
  enabled: true,

  // 是否显示时间戳
  showTimestamp: true,

  // 是否显示命名空间
  showNamespace: true,

  // 命名空间过滤（为空则显示所有，否则只显示匹配的）
  namespaceFilter: [],
}

// 从 localStorage 读取配置（持久化）
try {
  const storedConfig = localStorage.getItem('app:logger:config')
  if (storedConfig) {
    Object.assign(globalConfig, JSON.parse(storedConfig))
  }
} catch (e) {
  // ignore
}

// 从 URL 参数读取配置（优先级最高）
if (typeof window !== 'undefined') {
  const params = new URLSearchParams(window.location.search)

  if (params.has('debug')) {
    globalConfig.enabled = params.get('debug') !== 'false'
    globalConfig.level = LogLevel.DEBUG
  }

  if (params.has('log-level')) {
    const level = params.get('log-level').toUpperCase()
    if (LogLevel[level] !== undefined) {
      globalConfig.level = LogLevel[level]
    }
  }

  if (params.has('log-namespace')) {
    globalConfig.namespaceFilter = params.get('log-namespace').split(',')
  }
}

// ========== 日志工具类 ==========

class Logger {
  constructor(namespace = 'App', config = {}) {
    this.namespace = namespace
    this.config = { ...globalConfig, ...config }
    this.styles = this.#generateStyles()
  }

  /**
   * 生成样式
   */
  #generateStyles() {
    // 根据命名空间生成颜色（简单哈希）
    const hash = this.namespace.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc)
    }, 0)

    const hue = Math.abs(hash) % 360
    const color = `hsl(${hue}, 70%, 50%)`

    return {
      namespace: `color: ${color}; font-weight: bold;`,
      timestamp: 'color: #999; font-size: 0.9em;',
      log: 'color: inherit;',
      info: 'color: #2196F3;',
      warn: 'color: #FF9800;',
      error: 'color: #F44336; font-weight: bold;',
      debug: 'color: #9C27B0;',
    }
  }

  /**
   * 检查是否应该输出日志
   */
  #shouldLog(level) {
    if (!this.config.enabled) return false
    if (this.config.level < level) return false

    // 命名空间过滤
    if (this.config.namespaceFilter.length > 0) {
      return this.config.namespaceFilter.some(ns =>
        this.namespace.toLowerCase().includes(ns.toLowerCase())
      )
    }

    return true
  }

  /**
   * 格式化消息前缀
   */
  #formatPrefix() {
    const parts = []
    const styles = []

    // 时间戳
    if (this.config.showTimestamp) {
      const now = new Date()
      const timestamp = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}.${now.getMilliseconds().toString().padStart(3, '0')}`
      parts.push(`%c${timestamp}`)
      styles.push(this.styles.timestamp)
    }

    // 命名空间
    if (this.config.showNamespace) {
      parts.push(`%c[${this.namespace}]`)
      styles.push(this.styles.namespace)
    }

    return { prefix: parts.join(' '), styles }
  }

  /**
   * 输出日志
   */
  #output(level, levelName, style, args) {
    if (!this.#shouldLog(level)) return

    const { prefix, styles } = this.#formatPrefix()
    const levelPrefix = `%c${levelName}`

    const allStyles = [...styles, style]
    const message = `${prefix} ${levelPrefix}`

    // 使用对应的 console 方法
    const consoleMethod = levelName.toLowerCase()
    console[consoleMethod](message, ...allStyles, ...args)
  }

  /**
   * DEBUG 级别日志
   */
  debug(...args) {
    this.#output(LogLevel.DEBUG, 'DEBUG', this.styles.debug, args)
  }

  /**
   * LOG/INFO 级别日志
   */
  log(...args) {
    this.#output(LogLevel.INFO, 'LOG', this.styles.log, args)
  }

  /**
   * INFO 级别日志
   */
  info(...args) {
    this.#output(LogLevel.INFO, 'INFO', this.styles.info, args)
  }

  /**
   * WARN 级别日志
   */
  warn(...args) {
    this.#output(LogLevel.WARN, 'WARN', this.styles.warn, args)
  }

  /**
   * ERROR 级别日志
   */
  error(...args) {
    this.#output(LogLevel.ERROR, 'ERROR', this.styles.error, args)
  }

  /**
   * 分组日志（开始）
   */
  group(label, ...args) {
    if (this.#shouldLog(LogLevel.INFO)) {
      const { prefix, styles } = this.#formatPrefix()
      console.group(`${prefix} %c${label}`, ...styles, this.styles.log, ...args)
    }
  }

  /**
   * 分组日志（折叠）
   */
  groupCollapsed(label, ...args) {
    if (this.#shouldLog(LogLevel.INFO)) {
      const { prefix, styles } = this.#formatPrefix()
      console.groupCollapsed(`${prefix} %c${label}`, ...styles, this.styles.log, ...args)
    }
  }

  /**
   * 分组日志（结束）
   */
  groupEnd() {
    if (this.#shouldLog(LogLevel.INFO)) {
      console.groupEnd()
    }
  }

  /**
   * 表格输出
   */
  table(data) {
    if (this.#shouldLog(LogLevel.INFO)) {
      const { prefix, styles } = this.#formatPrefix()
      console.log(`${prefix}`, ...styles)
      console.table(data)
    }
  }

  /**
   * 时间测量（开始）
   */
  time(label) {
    if (this.#shouldLog(LogLevel.DEBUG)) {
      const timerLabel = `${this.namespace}:${label}`
      console.time(timerLabel)
    }
  }

  /**
   * 时间测量（结束）
   */
  timeEnd(label) {
    if (this.#shouldLog(LogLevel.DEBUG)) {
      const timerLabel = `${this.namespace}:${label}`
      console.timeEnd(timerLabel)
    }
  }

  /**
   * 创建子 Logger
   */
  createChild(childNamespace) {
    return new Logger(`${this.namespace}:${childNamespace}`, this.config)
  }
}

// ========== 导出 API ==========

/**
 * 创建 Logger 实例
 * @param {string} namespace - 命名空间
 * @param {Object} config - 配置（可选）
 * @returns {Logger}
 */
export function createLogger(namespace, config) {
  return new Logger(namespace, config)
}

/**
 * 默认 Logger
 */
export const logger = createLogger('App')

/**
 * 设置全局日志级别
 * @param {number} level - 日志级别
 */
export function setLogLevel(level) {
  globalConfig.level = level

  // 持久化到 localStorage
  try {
    localStorage.setItem('app:logger:config', JSON.stringify(globalConfig))
  } catch (e) {
    // ignore
  }
}

/**
 * 启用/禁用日志
 * @param {boolean} enabled - 是否启用
 */
export function setLogEnabled(enabled) {
  globalConfig.enabled = enabled

  try {
    localStorage.setItem('app:logger:config', JSON.stringify(globalConfig))
  } catch (e) {
    // ignore
  }
}

/**
 * 设置命名空间过滤
 * @param {string[]} namespaces - 命名空间数组
 */
export function setNamespaceFilter(namespaces) {
  globalConfig.namespaceFilter = namespaces

  try {
    localStorage.setItem('app:logger:config', JSON.stringify(globalConfig))
  } catch (e) {
    // ignore
  }
}

/**
 * 获取当前配置
 * @returns {Object}
 */
export function getLogConfig() {
  return { ...globalConfig }
}

// 在开发环境下暴露到全局（便于调试）
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__logger__ = {
    setLogLevel,
    setLogEnabled,
    setNamespaceFilter,
    getLogConfig,
    LogLevel,
  }
}

export default createLogger
